import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { showErrorToast } from '../../../components/common/BaseToaster';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../components/PageHeader';
import RobsForm from '../../../components/robs/RobsForm';
import SelectTypeModal from '../../../components/SelectTypeModal';
import {
  useGetAllManagersQuery,
  useGetAvailableDatesForRobQuery,
  useGetVesselByManagerIdQuery,
} from '../../../store/apiSlices/robs/robsApis';
import { isAdmin } from '../../../utils/roles';
import RobsProductListingContainer from './RobsProductListingContainer';

// Robs types data
const ROBS_TYPES = [
  { value: 'standard', label: 'robs.standardLabel' },
  { value: 'endOfManagement', label: 'robs.endOfManagementLabel' },
  { value: 'newBuilding', label: 'robs.newBuildingLabel' },
];

interface FormValues {
  vessel: string;
  manager: string | number | any;
  date: Date | string | any | null; // Date, string, or dayjs object, can be null
  remarks: string;
}

const AddRobsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in edit mode
  const isEditMode = location.state?.isEdit === true;
  const editData = location.state?.robsData;

  // State management
  const [currentStep, setCurrentStep] = useState<number>(isEditMode ? 2 : 1); // 1: type selection, 2: form, 3: products
  const [selectedType, setSelectedType] = useState<string>(editData?.type || '');
  const [isDataPrefilled, setIsDataPrefilled] = useState<boolean>(false);

  // useForm lives in container; pass control/errors to form
  const methods = useForm<FormValues>({
    defaultValues: {
      vessel: '',
      manager: null,
      date: null,
      remarks: '',
    },
  });

  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState<boolean>(false);
  const [typeSelectionError, setTypeSelectionError] = useState<string | null>(null);

  const { data: managers = [], refetch: refetchManagers } = useGetAllManagersQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const managerValue = methods.watch('manager');
  const vesselValue = methods.watch('vessel');
  const currentFormValues = methods.watch(); // Watch all form values for real-time updates

  const { data: vessels = [] } = useGetVesselByManagerIdQuery(parseInt(managerValue || ''), {
    skip: !managerValue || managerValue === '',
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { data: availableDates = {} } = useGetAvailableDatesForRobQuery(
    {
      managerId: parseInt(managerValue || ''),
      vesselId: parseInt(vesselValue || ''),
    },
    {
      skip: !managerValue || managerValue === '' || !vesselValue || vesselValue === '',
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const validateRobsType = useCallback(
    (type: string): string | true => {
      const validTypes = [
        'standard',
        'endOfManagement',
        'newBuilding',
        'secondHand',
        'EOM',
        'NEW',
        'ACQ',
        'Standard',
      ];
      if (!type || !validTypes.includes(type)) {
        return t('robs.validation.invalidType');
      }
      return true;
    },
    [t],
  );

  // Computed values
  const RobsTypeLabel = useMemo((): string => {
    const typeMap: Record<string, string> = {
      standard: t('robs.standardRob'),
      STANDARD: t('robs.standardRob'),
      endOfManagement: t('robs.endOfManagementRob'),
      EOM: t('robs.endOfManagementRob'),
      newBuilding: t('robs.newBuildingRob'),
      NEW: t('robs.newBuildingRob'),
      secondHand: t('robs.secondHandRob'),
      ACQ: t('robs.secondHandRob'),
    };
    return typeMap[selectedType] || '';
  }, [selectedType, t]);

  // Generate add button label based on selected type
  const getAddButtonLabel = useCallback(
    (type: string): string => {
      if (!type) return t('robs.genericAddRob');
      const robsType = ROBS_TYPES.find((t) => t.value === type);
      if (!robsType) return t('robs.genericAddRob');
      return t('robs.dynamicAddRob', { type: t(robsType.label) });
    },
    [t],
  );

  useEffect(() => {
    if (isEditMode) {
      setIsDataPrefilled(true);
      refetchManagers();
    }
  }, [isEditMode, refetchManagers]);

  // Map edit data display names to IDs when options are loaded
  useEffect(() => {
    if (isDataPrefilled && isEditMode && managers?.length) {
      const managerId = managers.find((m) => m.label === editData.manager)?.value || '';
      methods.setValue('vessel', editData?.vesselId);
      methods.setValue('manager', managerId as any);
      methods.setValue('date', editData?.date || null);
      methods.setValue('remarks', editData?.remarks || '');
      setIsDataPrefilled(false);
    }
  }, [isEditMode, editData, vessels, managers, isDataPrefilled, methods]);

  // Check for type in URL params and navigation state, and set initial state
  useEffect(() => {
    // Skip type selection modal in edit mode
    if (isEditMode) {
      setCurrentStep(3);
      return;
    }

    // Reset form when navigating to "Add ROB" from quick access (not in edit mode)
    if (location.pathname === '/rob/add') {
      // Reset all form data to initial state
      const initialFormData: FormValues = {
        vessel: '',
        manager: null,
        date: null,
        remarks: '',
      };

      methods.reset(initialFormData);

      // Reset selected type if not coming from URL or state
      if (!searchParams.get('type') && !location.state?.robsType) {
        setSelectedType('');
        setCurrentStep(1);
      }
    }

    // First check navigation state (from modal selection)
    const typeFromState = location.state?.robsType;
    if (typeFromState && validateRobsType(typeFromState) === true) {
      setSelectedType(typeFromState);
      setCurrentStep(2); // Go directly to form
      return;
    }

    // Then check URL parameters
    const typeFromUrl = searchParams.get('type');
    if (typeFromUrl && validateRobsType(typeFromUrl) === true) {
      setSelectedType(typeFromUrl);
      setCurrentStep(2); // Go directly to form
    } else {
      // Show type selection modal if no valid type is found
      setShowTypeSelectionModal(true);
    }
  }, [
    searchParams,
    location?.state,
    isEditMode,
    methods,
    location?.pathname,
    validateRobsType,
    navigate,
  ]);

  useEffect(() => {
    if (managers?.length > 0 && !isAdmin(userInfo.authorityName)) {
      const currentManager = managers.find((m: any) => m.label === userInfo.managerName)?.value;
      if (currentManager) {
        methods.setValue('manager', currentManager as any);
      }
    }
  }, [managers, methods, userInfo.authorityName, userInfo.managerName]);

  // Handle page reload/close warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only show alert if user is on the form (step 2) or products (step 3)
      if (currentStep === 2 || currentStep === 3) {
        e.preventDefault();
        // Modern browsers require returnValue to be set
        e.returnValue = '';
        // Some browsers will show this message, others will show a generic message
        return t('common.unsavedChanges');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep, t]);

  // Event handlers
  const handleTypeSelection = useCallback((type: string): void => {
    setSelectedType(type);
    setTypeSelectionError(null); // Clear error on selection
  }, []);

  const handleTypeConfirm = useCallback((): void => {
    // Validate robs type
    const typeValidation = validateRobsType(selectedType);
    if (typeValidation !== true) {
      setTypeSelectionError(typeValidation as string);
      showErrorToast(t('toast.validationError'), typeValidation);
      return;
    }

    setTypeSelectionError(null);
    setShowTypeSelectionModal(false);
    setCurrentStep(2); // Proceed to form
  }, [selectedType, t, validateRobsType]);

  const handleTypeModalClose = useCallback((): void => {
    setShowTypeSelectionModal(false);
    navigate('/rob');
  }, [navigate]);

  const handleTypeModalCancel = useCallback((): void => {
    setShowTypeSelectionModal(false);
    navigate('/rob');
  }, [navigate]);

  const handleFormSubmit = useCallback((_data: FormValues): void => {
    // proceed to products section; values remain in RHF state
    setCurrentStep(3);
  }, []);

  const handleFormElementSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void methods.handleSubmit(handleFormSubmit)();
  };

  const handleBackToForm = useCallback((): void => setCurrentStep(2), []);
  const handleCancel = useCallback((): void => {
    navigate('/rob');
  }, [navigate]);

  return (
    <>
      {/* Step 1: Robs Type Selection Modal */}
      <SelectTypeModal
        title={t('robs.selectType')}
        typesLabel={t('robs.robTypes')}
        open={showTypeSelectionModal}
        onClose={handleTypeModalClose}
        selectedType={selectedType}
        onChangeType={handleTypeSelection}
        onConfirm={handleTypeConfirm}
        onCancel={handleTypeModalCancel}
        isSubmitting={false}
        typeOptions={ROBS_TYPES.map((type) => ({
          ...type,
          label: t(type.label),
        }))}
        confirmButtonLabel={getAddButtonLabel(selectedType)}
        error={typeSelectionError}
      />

      {/* Header - shown for steps 2 and 3 */}
      {(currentStep === 2 || currentStep === 3) && (
        <Box sx={{ mb: 2 }}>
          <Box>
            <PageHeader
              options={[
                {
                  label: t('robs.title'),
                  isActive: false,
                  onClick: () => navigate('/rob'),
                },
                {
                  label: isEditMode ? t('robs.editRob') : t('robs.addRob'),
                  isActive: true,
                  onClick: () => {},
                },
              ]}
            />
          </Box>
        </Box>
      )}

      {/* Step 2: Robs Form */}
      {currentStep === 2 && (
        <RobsForm
          typeLabel={RobsTypeLabel}
          onSubmit={handleFormElementSubmit}
          onCancel={handleCancel}
          submitting={false}
          managers={managers}
          vessels={vessels}
          availableDates={availableDates as { startDate: string; endDate: string }}
          control={methods.control}
          errors={methods.formState.errors as any}
          setValues={methods.setValue}
          isAdmin={isAdmin(userInfo.authorityName)}
          robType={selectedType}
          isEditMode={isEditMode}
        />
      )}

      {/* Step 3: Products Section */}
      {currentStep === 3 && (
        <RobsProductListingContainer
          robsFormData={currentFormValues}
          selectedType={selectedType?.toLowerCase() === 'standard' ? 'Standard' : selectedType}
          onBack={handleBackToForm}
          vesselId={editData?.vesselId}
          vessels={vessels}
          managers={managers}
          isEditMode={isEditMode}
          editData={editData}
        />
      )}
    </>
  );
};

export default AddRobsContainer;
